// src/components/modals/EditClientModal/EditClientModal.tsx
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import styles from "./styles.module.css";
import { closeModal } from "src/store/slices/modalSlice";
import closeIcon from "../../../../assets/icons/closeGray.svg";
import PlusBlue from "../../../../assets/icons/plusBlue.svg";
import { Input } from "src/components/atoms/Input";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Select } from "src/components/atoms/Select";
import { itemsKids, itemsType } from "../AddClient/data";
import { useCallback, useMemo, useState, useEffect } from "react";
import { apiClient } from "src/api/client";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";
import { useDispatch } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { fetchClientDetail } from "src/store/slices/clientDetailSlice";
import useStates from "src/hooks/useStates";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type Option = {
  id: string | number;
  label: string;
};

type ChildField = {
  id: number;
  first_name: string;
  last_name: string;
  birth_date: string;
};

interface EditClientModalProps {
  onClose?: () => void;
}

const minBirthDate = new Date();
minBirthDate.setFullYear(minBirthDate.getFullYear() - 150);

const validationSchema = Yup.object({
  first_name: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  date_birth: Yup.date()
    .nullable()
    .required("Date of birth is required")
    .min(minBirthDate, "Age cannot exceed 150 years")
    .max(new Date(), "Date of birth cannot be in the future"),
  state_select: Yup.object().required("State is required"),
  marital_status_select: Yup.object().required("Marital status is required"),
  kids_select: Yup.object().required("Please select kids option"),
});

export const EditClientModal: React.FC<EditClientModalProps> = ({
  onClose,
}) => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const { states, loading: loadingStates, error: statesError } = useStates();

  const modalData = useAppSelector(
    (state) => state.modal.data.editClientModal as any
  );

  const clientId = modalData?.clientId;
  const clientData = modalData?.clientData;

  const [childrenFields, setChildrenFields] = useState<ChildField[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Преобразуем штаты в опции
  const statesOptions = useMemo(() => {
    return states.map((state) => ({
      id: state.id,
      label: state.name,
    }));
  }, [states]);

  const fallbackStates = [
    { id: 1, label: "California" },
    { id: 2, label: "New York" },
    { id: 3, label: "Texas" },
    { id: 4, label: "Florida" },
    { id: 5, label: "Illinois" },
  ];

  const statesToUse = statesOptions.length > 0 ? statesOptions : fallbackStates;

  // Инициализация детей и компаний
  useEffect(() => {
    if (clientData) {
      // Дети
      if (clientData.childrens && Array.isArray(clientData.childrens)) {
        const children = clientData.childrens.map(
          (child: any, index: number) => ({
            id: child.id || index + 1,
            first_name: child.first_name || "",
            last_name: child.last_name || "",
            birth_date: child.birth_date || "",
          })
        );
        setChildrenFields(children);
      } else {
        setChildrenFields([]);
      }

    }
  }, [clientData, states]);

  // Начальные значения для formik
  const initialValues = useMemo(() => {
    if (!clientData || statesToUse.length === 0) {
      return {
        first_name: "",
        last_name: "",
        date_birth: null as Date | null,
        state_id: 0,
        partner_first_name: "",
        partner_last_name: "",
        partner_birth_date: null as Date | null,
        partner_gender: "male" as "male" | "female",
        state_select: null as Option | null,
        marital_status_select: itemsType[0],
        kids_select: itemsKids[0],
      };
    }

    // Определяем marital status
    let maritalStatus = itemsType[0];
    const hasPartner =
      clientData.partner &&
      (clientData.partner.first_name || clientData.partner.last_name);

    if (hasPartner) {
      maritalStatus = itemsType[1];
    }

    // Определяем kids status
    let kidsSelection = itemsKids[1]; // No по умолчанию
    if (
      clientData.childrens &&
      Array.isArray(clientData.childrens) &&
      clientData.childrens.length > 0
    ) {
      kidsSelection = itemsKids[0]; // Yes, если есть дети
    }

    // Находим соответствующий штат
    const selectedState =
      statesToUse.find(
        (state) => String(state.id) === String(clientData.state_id)
      ) ||
      statesToUse[0] ||
      fallbackStates[0];

    return {
      first_name: clientData.first_name || "",
      last_name: clientData.last_name || "",
      date_birth: clientData.date_birth ? new Date(clientData.date_birth) : null,
      state_id: clientData.state_id || 0,
      partner_first_name: clientData.partner?.first_name || "",
      partner_last_name: clientData.partner?.last_name || "",
      partner_birth_date: clientData.partner?.birth_date ? new Date(clientData.partner.birth_date) : null,
      partner_gender: clientData.partner?.gender || "male",
      state_select: selectedState,
      marital_status_select: maritalStatus,
      kids_select: kidsSelection,
    };
  }, [clientData, statesToUse]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!clientId) {
        console.error("No clientId available");
        return;
      }

      setIsSubmitting(true);

      const updateData: any = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        date_birth: values.date_birth ? (values.date_birth as Date).toISOString().split("T")[0] : "",
        state_id: Number(values.state_select?.id || values.state_id),
      };

      if (isMarried && values.partner_first_name && values.partner_last_name) {
        updateData.partner = {
          first_name: values.partner_first_name.trim(),
          last_name: values.partner_last_name.trim(),
          birth_date: values.partner_birth_date ? (values.partner_birth_date as Date).toISOString().split("T")[0] : "",
          gender: values.partner_gender,
          id: clientData?.partner?.id || 0,
        };
      } else {
        updateData.partner = null;
      }

      if (hasKids && childrenFields.length > 0) {
        updateData.childrens = childrenFields
          .filter(
            (child) =>
              child.first_name.trim() &&
              child.last_name.trim() &&
              child.birth_date
          )
          .map((child) => ({
            first_name: child.first_name.trim(),
            last_name: child.last_name.trim(),
            birth_date: child.birth_date,
            id: child.id > 0 ? child.id : 0,
          }));
      } else {
        updateData.childrens = [];
      }

      try {
        await apiClient.updateClient(clientId, updateData);
        appDispatch(fetchClientDetail(clientId));
        dispatch(closeModal({ type: "editClientModal" }));
        if (onClose) onClose();
      } catch (error: any) {
        setSubmitError(getErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const createSelectHandler = useCallback(
    (items: Option[], nameTarget: string) => (id: string | number | null) => {
      const selectedOption = items.find(
        (item) => String(item.id) === String(id)
      );
      if (selectedOption) {
        formik.setFieldValue(nameTarget, selectedOption);
        if (nameTarget === "state_select") {
          formik.setFieldValue("state_id", id);
        }
      }
    },
    [formik]
  );

  const stateSelectHandler = useMemo(
    () => createSelectHandler(statesToUse, "state_select"),
    [createSelectHandler, statesToUse]
  );

  const maritalStatusHandler = useMemo(
    () => createSelectHandler(itemsType, "marital_status_select"),
    [createSelectHandler, itemsType]
  );

  const kidsSelectHandler = (id: string | number | null) => {
    const selectedOption = itemsKids.find(
      (item) => String(item.id) === String(id)
    );
    if (selectedOption) {
      formik.setFieldValue("kids_select", selectedOption);
      formik.setFieldTouched("kids_select", true, false);
      if (selectedOption.id === 1 && childrenFields.length === 0) {
        setChildrenFields([{ id: 1, first_name: "", last_name: "", birth_date: "" }]);
      }
    }
  };

  const isMarried = formik.values.marital_status_select?.id === 2;
  const hasKids = formik.values.kids_select?.id === 1;

  const addChildField = () => {
    const newId =
      childrenFields.length > 0
        ? Math.max(...childrenFields.map((c) => c.id)) + 1
        : 1;
    setChildrenFields((prev) => [
      ...prev,
      { id: newId, first_name: "", last_name: "", birth_date: "" },
    ]);
  };

  const removeChildField = (id: number) => {
    if (childrenFields.length > 1) {
      setChildrenFields((prev) => prev.filter((child) => child.id !== id));
    }
  };

  const updateChildField = (
    id: number,
    field: keyof ChildField,
    value: string
  ) => {
    setChildrenFields((prev) =>
      prev.map((child) =>
        child.id === id ? { ...child, [field]: value } : child
      )
    );
  };

  const handleClose = () => {
    dispatch(closeModal({ type: "editClientModal" }));
    if (onClose) onClose();
  };

  const getError = (touched: any, error: any): string | undefined => {
    return touched && error ? String(error) : undefined;
  };

  const isLoading = loadingStates;
  const loadError = statesError;

  if (isLoading) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3>Edit client</h3>
            <div>
              <Button
                text=""
                icon={closeIcon}
                btnType={BUTTONTYPE.WITHOUT_BORDER}
                onClick={handleClose}
              />
            </div>
          </div>
          <div className={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3>Edit client</h3>
            <div>
              <Button
                text=""
                icon={closeIcon}
                btnType={BUTTONTYPE.WITHOUT_BORDER}
                onClick={handleClose}
              />
            </div>
          </div>
          <ErrorBlock error={loadError} />
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3>Edit client</h3>
            <div>
              <Button
                text=""
                icon={closeIcon}
                btnType={BUTTONTYPE.WITHOUT_BORDER}
                onClick={handleClose}
              />
            </div>
          </div>
          <div className={styles.loading}>Error: No client data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Edit client</h3>
          <div>
            <Button
              text=""
              icon={closeIcon}
              btnType={BUTTONTYPE.WITHOUT_BORDER}
              onClick={handleClose}
            />
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className={styles.form}>
          <Input
            value={formik.values.first_name}
            label="First name"
            onChange={formik.handleChange}
            name="first_name"
            error={getError(
              formik.touched.first_name,
              formik.errors.first_name
            )}
          />

          <Input
            value={formik.values.last_name}
            label="Last name"
            onChange={formik.handleChange}
            name="last_name"
            error={getError(formik.touched.last_name, formik.errors.last_name)}
          />

          <div className={styles.datePickerWrapper}>
            <label className={styles.datePickerLabel}>Date of birth</label>
            <DatePicker
              selected={formik.values.date_birth}
              onChange={(date) => {
                formik.setFieldValue("date_birth", date);
                formik.setFieldTouched("date_birth", true, false);
              }}
              dateFormat="MMM dd, yyyy"
              minDate={minBirthDate}
              maxDate={new Date()}
              placeholderText="Select date of birth"
              className={styles.datePickerInput}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
            />
            {formik.touched.date_birth && formik.errors.date_birth && (
              <div className={styles.errorText}>
                {formik.errors.date_birth as string}
              </div>
            )}
          </div>

          <Select
            label="State"
            items={statesToUse}
            onSelect={stateSelectHandler}
            selectedId={formik.values.state_select?.id}
          />

          <Select
            label="Marital status"
            items={itemsType}
            onSelect={maritalStatusHandler}
            selectedId={formik.values.marital_status_select?.id}
          />

          {isMarried && (
            <>
              <div className={styles.inputGrid}>
                <Input
                  value={formik.values.partner_first_name}
                  label="Partner first name"
                  onChange={formik.handleChange}
                  name="partner_first_name"
                  className={styles.whiteInput}
                  error={getError(
                    formik.touched.partner_first_name,
                    formik.errors.partner_first_name
                  )}
                />

                <Input
                  value={formik.values.partner_last_name}
                  label="Partner last name"
                  onChange={formik.handleChange}
                  name="partner_last_name"
                  className={styles.whiteInput}
                  error={getError(
                    formik.touched.partner_last_name,
                    formik.errors.partner_last_name
                  )}
                />
              </div>

              <div className={styles.inputGrid}>
                <div className={styles.datePickerWrapper}>
                  <label className={styles.datePickerLabel}>Partner birth date</label>
                  <DatePicker
                    selected={formik.values.partner_birth_date}
                    onChange={(date) => {
                      formik.setFieldValue("partner_birth_date", date);
                      formik.setFieldTouched("partner_birth_date", true, false);
                    }}
                    dateFormat="MMM dd, yyyy"
                    minDate={minBirthDate}
                    maxDate={new Date()}
                    placeholderText="Select birth date"
                    className={styles.datePickerInput}
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                  />
                  {formik.touched.partner_birth_date && formik.errors.partner_birth_date && (
                    <div className={styles.errorText}>
                      {formik.errors.partner_birth_date as string}
                    </div>
                  )}
                </div>

                <Select
                  label="Partner gender"
                  items={[
                    { id: "male", label: "Male" },
                    { id: "female", label: "Female" },
                  ]}
                  onSelect={(id) => formik.setFieldValue("partner_gender", id)}
                  selectedId={formik.values.partner_gender}
                  className={styles.whiteInput}
                />
              </div>
            </>
          )}

          <Select
            label="Kids"
            items={itemsKids}
            onSelect={kidsSelectHandler}
            selectedId={formik.values.kids_select?.id}
          />

          {hasKids && (
            <>
              {childrenFields.map((child, index) => (
                <div key={child.id} className={styles.childSection}>
                  <div className={styles.childHeader}>
                    <h4>Child {index + 1}</h4>
                    {childrenFields.length > 1 && (
                      <Button
                        text="Remove"
                        btnType={BUTTONTYPE.WITHOUT_BORDER}
                        onClick={() => removeChildField(child.id)}
                        classname={styles.removeButton}
                      />
                    )}
                  </div>

                  <div className={styles.inputGrid}>
                    <Input
                      value={child.first_name}
                      label="Child first name"
                      onChange={(e) =>
                        updateChildField(child.id, "first_name", e.target.value)
                      }
                      className={styles.whiteInput}
                    />

                    <Input
                      value={child.last_name}
                      label="Child last name"
                      onChange={(e) =>
                        updateChildField(child.id, "last_name", e.target.value)
                      }
                      className={styles.whiteInput}
                    />

                    <div className={styles.datePickerWrapper}>
                      <label className={styles.datePickerLabel}>Child birth date</label>
                      <DatePicker
                        selected={child.birth_date ? new Date(child.birth_date) : null}
                        onChange={(date) => {
                          const val = date ? date.toISOString().split("T")[0] : "";
                          updateChildField(child.id, "birth_date", val);
                        }}
                        dateFormat="MMM dd, yyyy"
                        minDate={minBirthDate}
                        maxDate={new Date()}
                        placeholderText="Select birth date"
                        className={styles.datePickerInput}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className={styles.addChildButton}>
                <Button
                  text="Add another child"
                  icon={PlusBlue}
                  btnType={BUTTONTYPE.WITHOUT_BORDER}
                  onClick={addChildField}
                />
              </div>
            </>
          )}

          <ErrorBlock error={submitError} />
          <Button
            text={isSubmitting ? "Saving changes..." : "Save changes"}
            type="submit"
            disabled={!formik.isValid || isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};
